import { ManagedDialogHandler } from './managed-dialog-handler';
import { ModalVisibilityObserver } from './modal-visibility-observer';

enum DialogType {
    Confirmation = 'confirmation',
    Error = 'error',
    Input = 'input',
    Success = 'success'
}

interface IDialogConfig {
    title: string;
    type: DialogType;
    closeLabel: string;
    buttonLabel: string;
    confirmLink: string;
    replaceConfirmLink?: boolean;
    preventBackdropClose?: boolean;
    customId?: string;
    showDialogTriggerId?: string;
}

/**
 * Stores HTMX event listener references for cleanup
 */
const htmxEventListeners: Map<string, () => void> = new Map();

/**
 * Initializes a dialog with the specified configuration
 * @param dialogConfigDataId - The ID of the element containing the dialog configuration
 * @param closeDialogButtonPropName - The property name of the close dialog button
 */
(window as any)['dialogInit'] = (
    dialogConfigDataId: string,
    closeDialogButtonPropName: string
): void => {
    try {
        const configElement: HTMLElement | null =
            document.getElementById(dialogConfigDataId);
        if (!configElement) {
            console.error(
                `Dialog config element with ID "${dialogConfigDataId}" not found`
            );
            return;
        }

        const dialogConfig: IDialogConfig = JSON.parse(
            configElement.textContent || '{}'
        ) as IDialogConfig;

        if (!dialogConfig || !dialogConfig.closeLabel) {
            console.error('Invalid dialog configuration');
            return;
        }

        const dialogId: string = dialogConfig.customId || 'dialog';
        const dialogPropName: string = `${dialogConfig.customId || ''}-openDialog`;

        console.log(
            `[DialogInit] Initializing dialog: ${dialogId}, property: ${dialogPropName}`
        );

        // Clean up any existing HTMX event listener for this dialog
        const existingListener: (() => void) | undefined =
            htmxEventListeners.get(dialogPropName);
        if (existingListener) {
            console.log(
                `[DialogInit] Cleaning up existing listener for ${dialogPropName}`
            );
            (window as any)['htmx'].off(
                'htmx:replacedInHistory',
                existingListener
            );
            htmxEventListeners.delete(dialogPropName);
        }

        if (
            dialogConfig.confirmLink &&
            !dialogConfig.replaceConfirmLink &&
            !dialogConfig.showDialogTriggerId
        ) {
            // If there is a regular link (no HTMX replace, no trigger), just use the raw dialog element
            // The navigation will do any event handler cleanup
            (window as any)[dialogPropName] = document.getElementById(dialogId);
        } else {
            try {
                (window as any)[dialogPropName] = new ManagedDialogHandler(
                    dialogId,
                    dialogConfig.customId
                        ? `#${dialogConfig.customId}_closeButton`
                        : '#closeButton',
                    Boolean(dialogConfig.showDialogTriggerId) &&
                        dialogConfig.showDialogTriggerId?.toString() !== 'event'
                        ? `#${dialogConfig.showDialogTriggerId}`
                        : undefined,
                    dialogConfig.preventBackdropClose || false
                );

                // Register the dialog with the ModalVisibilityObserver
                // to handle visibility changes and cleanup
                // when the dialog is closed.
                // This is useful for dialogs that are not closed by a link.
                new ModalVisibilityObserver().register(dialogId);

                if (
                    dialogConfig.confirmLink &&
                    dialogConfig.replaceConfirmLink
                ) {
                    // Create a named function for the event listener so we can remove it later
                    const replaceHistoryListener: () => void = (): void => {
                        try {
                            const dialogInstance = (window as any)[
                                dialogPropName
                            ];
                            // Only try to close if the dialog still exists, is a ManagedDialogHandler, AND is currently open
                            if (
                                dialogInstance &&
                                typeof dialogInstance.close === 'function' &&
                                typeof dialogInstance.isOpen === 'function' &&
                                dialogInstance.isOpen()
                            ) {
                                dialogInstance.close();
                            }
                        } catch (error) {
                            console.warn(
                                `Failed to close dialog "${dialogId}" on history replace:`,
                                error
                            );
                        }
                    };

                    // Store the listener so we can remove it later
                    htmxEventListeners.set(
                        dialogPropName,
                        replaceHistoryListener
                    );

                    // Register the event listener
                    (window as any)['htmx'].on(
                        'htmx:replacedInHistory',
                        replaceHistoryListener
                    );
                }
            } catch (error) {
                console.error(
                    `Failed to initialize dialog "${dialogId}":`,
                    error
                );
                return;
            }
        }

        if (!Boolean(dialogConfig.showDialogTriggerId)) {
            (window as any)[dialogPropName]?.show?.();
        }

        const closeDialogButton: HTMLElement | null = document.getElementById(
            closeDialogButtonPropName
        );

        closeDialogButton?.addEventListener('click', (): void => {
            const dialogInstance = (window as any)[dialogPropName];
            if (dialogInstance && typeof dialogInstance.close === 'function') {
                try {
                    dialogInstance.close();
                } catch (error) {
                    console.warn(
                        `Failed to close dialog "${dialogId}":`,
                        error
                    );
                }
            }
        });
    } catch (error) {
        console.error('Error initializing dialog:', error);
    }
};

/**
 * Named handler for closing dialogs before HTMX requests
 * Allows for duplicate detection and removal if needed
 */
const handleDialogCloseBeforeRequest = (e: Event): void => {
    const customEvent = e as CustomEvent;
    const target = customEvent.detail?.elt as HTMLElement;

    if (target && target.hasAttribute('data-close-dialog-id')) {
        const dialogId: string =
            target.getAttribute('data-close-dialog-id') || '';
        const dialog = document.getElementById(dialogId) as HTMLDialogElement;

        if (dialog && dialog.open) {
            console.log(
                `[DialogClose] Closing dialog "${dialogId}" before HTMX request`
            );
            dialog.close();
        }
    }
};

/**
 * Set up global HTMX event listener to close dialogs before requests
 * This allows confirmation dialogs to close before error dialogs appear
 */
(() => {
    // Guard against duplicate registration
    if ((window as any)['_dialogBeforeRequestListenerSetup']) {
        console.warn(
            '[DialogClose] Event listener already registered, skipping duplicate setup'
        );
        return;
    }
    (window as any)['_dialogBeforeRequestListenerSetup'] = true;

    // Remove any existing listener first (defensive programming)
    document.body.removeEventListener(
        'htmx:beforeRequest',
        handleDialogCloseBeforeRequest
    );

    // Register the listener
    document.body.addEventListener(
        'htmx:beforeRequest',
        handleDialogCloseBeforeRequest
    );

    console.log('[DialogClose] HTMX beforeRequest listener registered');
})();
